import React, { useState, useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import toast from 'react-hot-toast';
import { AppDispatch, RootState } from '../../app/store';
import {
  createRequisitionAction,
  CreateRequisitionPayload,
} from './requisitionSlice';
import apiClient from '../../services/api';
import { fetchReferenceData } from '../../services/referenceDataCache';

interface Category {
  id: number;
  name: string;
}

interface SubCategory {
  id: number;
  name: string;
  requisition_category_id: number;
}

interface Store {
  id: number;
  name: string;
  storenumber?: string;
}

interface Equipment {
  id: number;
  description: string;
  serial_number?: string;
  category?: { name: string };
  subCategory?: { name: string };
}

interface Supplier {
  id: number;
  name: string;
}

interface Technician {
  id: number;
  name: string;
}

interface FormData {
  category: number;
  sub_category: number;
  internal: number;
  is_repair: number;
  selectedStore: number | null;
  assetsToRepair: string[];
  descriptionOfRepairs: string[];
  selected_equipment: Array<{
    id: number;
    description: string;
    category: string;
    subcategory: string;
    serial_number: string;
  }>;
  equipment_descriptions: string[];
  selected_asset: number;
  hasQuotation: boolean;
  quotation_date: string;
  repair_cost: number;
  quotation_number: string;
  vat: number;
  attachment: File | null;
  requisition_date: string;
  technician_id: number | null;
  supplier_id: number | null;
}

const CreateRequisitionPage: React.FC = () => {
  const dispatch: AppDispatch = useDispatch();
  const navigate = useNavigate();
  const { isUpdating } = useSelector((state: RootState) => state.requisitions);

  const [formData, setFormData] = useState<FormData>({
    category: 0,
    sub_category: 0,
    internal: 1,
    is_repair: 1,
    selectedStore: null,
    assetsToRepair: [''],
    descriptionOfRepairs: [''],
    selected_equipment: [],
    equipment_descriptions: [],
    selected_asset: 0,
    hasQuotation: false,
    quotation_date: '',
    repair_cost: 0,
    quotation_number: '',
    vat: 0,
    attachment: null,
    requisition_date: new Date().toISOString().split('T')[0],
    technician_id: null,
    supplier_id: null,
  });

  const [categories, setCategories] = useState<Category[]>([]);
  const [subCategories, setSubCategories] = useState<SubCategory[]>([]);
  const [stores, setStores] = useState<Store[]>([]);
  const [suppliers, setSuppliers] = useState<Supplier[]>([]);
  const [technicians, setTechnicians] = useState<Technician[]>([]);
  const [equipment, setEquipment] = useState<Equipment[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const loadMeta = async () => {
      try {
        const [categories, stores, suppliers, technicians] = await Promise.all([
          fetchReferenceData<Category[]>('categories', '/requisitions/categories'),
          fetchReferenceData<Store[]>('stores', '/requisitions/stores'),
          fetchReferenceData<Supplier[]>('suppliers', '/requisitions/suppliers'),
          fetchReferenceData<Technician[]>('technicians', '/requisitions/technicians'),
        ]);
        setCategories(categories);
        setStores(stores);
        setSuppliers(suppliers);
        setTechnicians(technicians);
      } catch (err) {
        setError('Failed to load form data');
      } finally {
        setLoading(false);
      }
    };
    loadMeta();
  }, []);

  useEffect(() => {
    if (formData.category > 0) {
      apiClient
        .get(`/requisitions/subcategories?category_id=${formData.category}`)
        .then((res) => setSubCategories(res.data.data || []))
        .catch(() => setError('Failed to load subcategories'));
    } else {
      setSubCategories([]);
    }
    setFormData((prev) => ({ ...prev, sub_category: 0 }));
  }, [formData.category]);

  useEffect(() => {
    if (formData.category === 10 && formData.selectedStore) {
      const params = new URLSearchParams();
      params.append('store_ids[]', formData.selectedStore.toString());
      if (formData.sub_category) {
        params.append('subcategory_id', formData.sub_category.toString());
      }
      apiClient
        .get(`/requisitions/equipment?${params.toString()}`)
        .then((res) => setEquipment(res.data.data || []))
        .catch(() => setError('Failed to load equipment'));
    } else {
      setEquipment([]);
    }
  }, [formData.category, formData.selectedStore, formData.sub_category]);

  const handleInputChange = (field: keyof FormData, value: any) => {
    setFormData((prev) => ({ ...prev, [field]: value }));
  };

  const handleArrayInputChange = (
    field: 'assetsToRepair' | 'descriptionOfRepairs' | 'equipment_descriptions',
    index: number,
    value: string
  ) => {
    setFormData((prev) => ({
      ...prev,
      [field]: prev[field].map((item, i) => (i === index ? value : item)),
    }));
  };

  const handleStoreChange = (storeId: number) => {
    setFormData((prev) => ({
      ...prev,
      selectedStore: storeId || null,
    }));
  };

  const handleFileChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0] || null;
    if (file && file.type !== 'application/pdf') {
      setError('Please select a valid PDF file');
      event.target.value = '';
      return;
    }
    if (file && file.size > 10 * 1024 * 1024) {
      setError('File size must be less than 10MB');
      event.target.value = '';
      return;
    }
    setError(null);
    setFormData((prev) => ({ ...prev, attachment: file }));
  };

  const addAssetToRepair = () => {
    setFormData((prev) => ({
      ...prev,
      assetsToRepair: [...prev.assetsToRepair, ''],
      descriptionOfRepairs: [...prev.descriptionOfRepairs, ''],
    }));
  };

  const removeAssetToRepair = (index: number) => {
    setFormData((prev) => ({
      ...prev,
      assetsToRepair: prev.assetsToRepair.filter((_, i) => i !== index),
      descriptionOfRepairs: prev.descriptionOfRepairs.filter((_, i) => i !== index),
    }));
  };

  const addSelectedEquipment = () => {
    if (formData.selected_asset === 0) return;
    const selectedEquipment = equipment.find((eq) => eq.id === formData.selected_asset);
    if (!selectedEquipment) return;

    const isDuplicate = formData.selected_equipment.some(
      (existing) => existing.serial_number === selectedEquipment.serial_number
    );
    if (isDuplicate) {
      setError(`Equipment with serial number "${selectedEquipment.serial_number}" is already added.`);
      return;
    }

    setFormData((prev) => ({
      ...prev,
      selected_equipment: [
        ...prev.selected_equipment,
        {
          id: selectedEquipment.id,
          description: selectedEquipment.description,
          category: selectedEquipment.category?.name || '',
          subcategory: selectedEquipment.subCategory?.name || '',
          serial_number: selectedEquipment.serial_number || '',
        },
      ],
      equipment_descriptions: [...prev.equipment_descriptions, ''],
      selected_asset: 0,
    }));
    setError(null);
  };

  const removeSelectedEquipment = (index: number) => {
    setFormData((prev) => ({
      ...prev,
      selected_equipment: prev.selected_equipment.filter((_, i) => i !== index),
      equipment_descriptions: prev.equipment_descriptions.filter((_, i) => i !== index),
    }));
  };

  const validate = (): boolean => {
    if (formData.category === 0) {
      setError('Please select a category');
      return false;
    }
    if (formData.sub_category === 0) {
      setError('Please select a sub category');
      return false;
    }
    if (!formData.selectedStore) {
      setError('Please select a store');
      return false;
    }
    if (formData.internal === 1 && !formData.technician_id) {
      setError('Please select a technician for internal requisitions');
      return false;
    }
    if (formData.internal === 2 && !formData.supplier_id) {
      setError('Please select a supplier for external requisitions');
      return false;
    }
    const hasAssets = formData.assetsToRepair.some((a) => a.trim() !== '') && formData.descriptionOfRepairs.some((d) => d.trim() !== '');
    const hasEquipment = formData.selected_equipment.length > 0;
    if (formData.category === 10 && !hasEquipment) {
      setError('Please add at least one equipment item');
      return false;
    }
    if (!hasEquipment && !hasAssets) {
      setError('Please add at least one asset and description');
      return false;
    }
    return true;
  };

  const handleSubmit = async () => {
    if (!validate()) return;

    // Filter out empty asset pairs so backend validation only sees meaningful entries
    const validAssetPairs = formData.assetsToRepair
      .map((asset, index) => ({
        asset: asset.trim(),
        description: (formData.descriptionOfRepairs[index] || '').trim(),
      }))
      .filter(pair => pair.asset !== '' || pair.description !== '');

    const payload: CreateRequisitionPayload = {
      category: formData.category,
      sub_category: formData.sub_category,
      internal: formData.internal,
      is_repair: formData.is_repair,
      selectedStores: formData.selectedStore ? [formData.selectedStore] : [],
      assetsToRepair: validAssetPairs.length > 0 ? validAssetPairs.map(pair => pair.asset) : [''],
      descriptionOfRepairs: validAssetPairs.length > 0 ? validAssetPairs.map(pair => pair.description) : [''],
      selected_equipment: formData.selected_equipment,
      equipment_descriptions: formData.equipment_descriptions,
      hasQuotation: formData.hasQuotation,
      quotation_date: formData.quotation_date,
      repair_cost: formData.repair_cost,
      quotation_number: formData.quotation_number,
      vat: formData.vat,
      attachment: formData.attachment,
      requisition_date: formData.requisition_date,
      technician_id: formData.technician_id,
      supplier_id: formData.supplier_id,
    };

    try {
      const result = await dispatch(createRequisitionAction(payload)).unwrap();
      toast.success('Requisition created successfully');
      navigate(`/requisitions/${result.requisition.id}`);
    } catch (err: any) {
      const errorMessage = err || 'Failed to create requisition';
      setError(errorMessage);
      toast.error(errorMessage);
    }
  };

  if (loading) {
    return (
      <div className="flex justify-center items-center h-64">
        <div className="animate-spin rounded-full h-10 w-10 border-b-2 border-blue-500"></div>
      </div>
    );
  }

  return (
    <div className="max-w-5xl mx-auto p-4">
      <div className="bg-white dark:bg-gray-800 rounded-lg shadow-md">
        <div className="p-4 border-b border-gray-200 dark:border-gray-700 flex justify-between items-center">
          <h1 className="text-xl font-bold text-gray-900 dark:text-white">Create New ATR</h1>
          <button
            onClick={() => navigate('/requisitions')}
            className="text-gray-600 hover:text-gray-800 dark:text-gray-300 dark:hover:text-white"
          >
            Cancel
          </button>
        </div>

        <div className="p-6 space-y-8">
          {error && (
            <div className="bg-red-100 border border-red-400 text-red-700 px-4 py-3 rounded whitespace-pre-wrap">
              {error}
            </div>
          )}

          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
              Store
            </label>
            <select
              value={formData.selectedStore || 0}
              onChange={(e) => handleStoreChange(parseInt(e.target.value))}
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 dark:bg-gray-700 dark:border-gray-600 dark:text-white"
            >
              <option value={0}>Select Store</option>
              {stores.map((store) => (
                <option key={store.id} value={store.id}>
                  {store.name}
                </option>
              ))}
            </select>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                Category
              </label>
              <select
                value={formData.category}
                onChange={(e) => handleInputChange('category', parseInt(e.target.value))}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 dark:bg-gray-700 dark:border-gray-600 dark:text-white"
              >
                <option value={0}>Select Category</option>
                {categories.map((cat) => (
                  <option key={cat.id} value={cat.id}>
                    {cat.name}
                  </option>
                ))}
              </select>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                Sub Category
              </label>
              <select
                value={formData.sub_category}
                onChange={(e) => handleInputChange('sub_category', parseInt(e.target.value))}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 dark:bg-gray-700 dark:border-gray-600 dark:text-white"
                disabled={formData.category === 0}
              >
                <option value={0}>Select Sub Category</option>
                {subCategories.map((sub) => (
                  <option key={sub.id} value={sub.id}>
                    {sub.name}
                  </option>
                ))}
              </select>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                Internal / External
              </label>
              <select
                value={formData.internal}
                onChange={(e) => handleInputChange('internal', parseInt(e.target.value))}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 dark:bg-gray-700 dark:border-gray-600 dark:text-white"
              >
                <option value={1}>Internal</option>
                <option value={2}>External</option>
              </select>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                Repair / New Purchase
              </label>
              <select
                value={formData.is_repair}
                onChange={(e) => handleInputChange('is_repair', parseInt(e.target.value))}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 dark:bg-gray-700 dark:border-gray-600 dark:text-white"
              >
                <option value={1}>Repair</option>
                <option value={0}>New Purchase</option>
              </select>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                Requisition Date
              </label>
              <input
                type="date"
                max={new Date().toISOString().split('T')[0]}
                value={formData.requisition_date}
                onChange={(e) => handleInputChange('requisition_date', e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 dark:bg-gray-700 dark:border-gray-600 dark:text-white"
              />
            </div>

            {formData.internal === 1 ? (
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                  Technician
                </label>
                <select
                  value={formData.technician_id || 0}
                  onChange={(e) => handleInputChange('technician_id', parseInt(e.target.value) || null)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 dark:bg-gray-700 dark:border-gray-600 dark:text-white"
                >
                  <option value={0}>Select Technician</option>
                  {technicians.map((tech) => (
                    <option key={tech.id} value={tech.id}>
                      {tech.name}
                    </option>
                  ))}
                </select>
              </div>
            ) : (
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                  Supplier
                </label>
                <select
                  value={formData.supplier_id || 0}
                  onChange={(e) => handleInputChange('supplier_id', parseInt(e.target.value) || null)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 dark:bg-gray-700 dark:border-gray-600 dark:text-white"
                >
                  <option value={0}>Select Supplier</option>
                  {suppliers.map((sup) => (
                    <option key={sup.id} value={sup.id}>
                      {sup.name}
                    </option>
                  ))}
                </select>
              </div>
            )}
          </div>

          {formData.category === 10 && formData.selectedStore && (
            <div className="bg-gray-50 dark:bg-gray-700 p-4 rounded-lg">
              <h3 className="text-lg font-medium text-gray-900 dark:text-white mb-3">Selected Equipment</h3>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-4">
                <div className="md:col-span-2">
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                    Select Equipment
                  </label>
                  <select
                    value={formData.selected_asset}
                    onChange={(e) => handleInputChange('selected_asset', parseInt(e.target.value))}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 dark:bg-gray-700 dark:border-gray-600 dark:text-white"
                  >
                    <option value={0}>-- Select Equipment --</option>
                    {equipment.map((eq) => (
                      <option key={eq.id} value={eq.id}>
                        {eq.description} {eq.serial_number ? `(S/N: ${eq.serial_number})` : '(No S/N)'}
                      </option>
                    ))}
                  </select>
                </div>
                <div className="flex items-end">
                  <button
                    type="button"
                    onClick={addSelectedEquipment}
                    disabled={formData.selected_asset === 0}
                    className="w-full px-4 py-2 bg-blue-500 text-white rounded hover:bg-blue-600 disabled:bg-blue-300"
                  >
                    Add Equipment
                  </button>
                </div>
              </div>

              {formData.selected_equipment.length > 0 && (
                <div className="overflow-x-auto">
                  <table className="min-w-full bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-600 rounded-lg">
                    <thead className="bg-gray-100 dark:bg-gray-700">
                      <tr>
                        <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase">Description</th>
                        <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase">Category</th>
                        <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase">Subcategory</th>
                        <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase">Serial Number</th>
                        <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase">Repair Description</th>
                        <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase">Action</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-gray-200 dark:divide-gray-600">
                      {formData.selected_equipment.map((eq, index) => (
                        <tr key={index}>
                          <td className="px-4 py-2 text-sm text-gray-900 dark:text-white">{eq.description}</td>
                          <td className="px-4 py-2 text-sm text-gray-900 dark:text-white">{eq.category}</td>
                          <td className="px-4 py-2 text-sm text-gray-900 dark:text-white">{eq.subcategory}</td>
                          <td className="px-4 py-2 text-sm text-gray-900 dark:text-white">{eq.serial_number || 'No S/N'}</td>
                          <td className="px-4 py-2">
                            <textarea
                              value={formData.equipment_descriptions[index] || ''}
                              onChange={(e) => handleArrayInputChange('equipment_descriptions', index, e.target.value)}
                              className="w-full px-2 py-1 border border-gray-300 rounded focus:outline-none focus:ring-2 focus:ring-blue-500 dark:bg-gray-700 dark:border-gray-600 dark:text-white"
                              rows={2}
                              placeholder="Enter repair description"
                            />
                          </td>
                          <td className="px-4 py-2">
                            <button
                              type="button"
                              onClick={() => removeSelectedEquipment(index)}
                              className="px-3 py-1 bg-red-500 text-white rounded hover:bg-red-600"
                            >
                              Remove
                            </button>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </div>
          )}

          {formData.category !== 10 && (
            <div>
              <div className="flex justify-between items-center mb-3">
                <h3 className="text-lg font-medium text-gray-900 dark:text-white">Assets to Repair</h3>
                <button
                  type="button"
                  onClick={addAssetToRepair}
                  className="px-3 py-1 bg-blue-500 text-white text-sm rounded hover:bg-blue-600"
                >
                  Add Asset
                </button>
              </div>
              <div className="space-y-3">
                {formData.assetsToRepair.map((asset, index) => (
                  <div key={index} className="grid grid-cols-1 md:grid-cols-2 gap-3">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                        Asset {index + 1}
                      </label>
                      <textarea
                        value={asset}
                        onChange={(e) => handleArrayInputChange('assetsToRepair', index, e.target.value)}
                        className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 dark:bg-gray-700 dark:border-gray-600 dark:text-white resize-y"
                        rows={3}
                        placeholder="Enter asset description"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                        Description {index + 1}
                      </label>
                      <div className="flex gap-2">
                        <textarea
                          value={formData.descriptionOfRepairs[index] || ''}
                          onChange={(e) => handleArrayInputChange('descriptionOfRepairs', index, e.target.value)}
                          className="flex-1 px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 dark:bg-gray-700 dark:border-gray-600 dark:text-white resize-y"
                          rows={3}
                          placeholder="Enter repair description"
                        />
                        <button
                          type="button"
                          onClick={() => removeAssetToRepair(index)}
                          className="px-3 py-2 bg-red-500 text-white rounded hover:bg-red-600"
                        >
                          Remove
                        </button>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}

          <div>
            <div className="flex items-center mb-3">
              <input
                type="checkbox"
                id="hasQuotation"
                checked={formData.hasQuotation}
                onChange={(e) => handleInputChange('hasQuotation', e.target.checked)}
                className="h-4 w-4 text-blue-600 border-gray-300 rounded focus:ring-blue-500"
              />
              <label htmlFor="hasQuotation" className="ml-2 text-sm font-medium text-gray-700 dark:text-gray-300">
                I have a quotation
              </label>
            </div>

            {formData.hasQuotation && (
              <>
                <h3 className="text-lg font-medium text-gray-900 dark:text-white mb-3">Quotation Details</h3>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                      Quotation Date
                    </label>
                    <input
                      type="date"
                      value={formData.quotation_date}
                      onChange={(e) => handleInputChange('quotation_date', e.target.value)}
                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 dark:bg-gray-700 dark:border-gray-600 dark:text-white"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                      Quotation Number
                    </label>
                    <input
                      type="text"
                      value={formData.quotation_number}
                      onChange={(e) => handleInputChange('quotation_number', e.target.value)}
                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 dark:bg-gray-700 dark:border-gray-600 dark:text-white"
                      placeholder="Enter quotation number"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                      Repair Cost
                    </label>
                    <input
                      type="number"
                      step="0.01"
                      value={formData.repair_cost}
                      onChange={(e) => handleInputChange('repair_cost', parseFloat(e.target.value) || 0)}
                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 dark:bg-gray-700 dark:border-gray-600 dark:text-white"
                      placeholder="Enter repair cost"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                      VAT
                    </label>
                    <select
                      value={formData.vat}
                      onChange={(e) => handleInputChange('vat', parseInt(e.target.value))}
                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 dark:bg-gray-700 dark:border-gray-600 dark:text-white"
                    >
                      <option value={0}>Exclusive</option>
                      <option value={1}>Inclusive</option>
                    </select>
                  </div>
                  <div className="md:col-span-2">
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                      Attach Quote (PDF only, max 10MB)
                    </label>
                    <input
                      type="file"
                      accept=".pdf,application/pdf"
                      onChange={handleFileChange}
                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 dark:bg-gray-700 dark:border-gray-600 dark:text-white file:mr-4 file:py-2 file:px-4 file:rounded-md file:border-0 file:text-sm file:font-medium file:bg-blue-50 file:text-blue-700 hover:file:bg-blue-100"
                    />
                    {formData.attachment && (
                      <p className="mt-1 text-sm text-green-600 dark:text-green-400">
                        Selected: {formData.attachment.name} ({(formData.attachment.size / 1024 / 1024).toFixed(2)} MB)
                      </p>
                    )}
                  </div>
                </div>
              </>
            )}
          </div>
        </div>

        <div className="p-4 border-t border-gray-200 dark:border-gray-700 flex justify-end gap-3">
          <button
            onClick={() => navigate('/requisitions')}
            className="px-4 py-2 bg-gray-100 hover:bg-gray-200 dark:bg-gray-700 dark:hover:bg-gray-600 text-gray-800 dark:text-gray-200 rounded-md"
          >
            Cancel
          </button>
          <button
            onClick={handleSubmit}
            disabled={isUpdating}
            className="px-4 py-2 bg-blue-500 hover:bg-blue-600 disabled:bg-blue-300 text-white rounded-md"
          >
            {isUpdating ? 'Creating...' : 'Create Requisition'}
          </button>
        </div>
      </div>
    </div>
  );
};

export default CreateRequisitionPage;
